<template>
  <ClientOnly>
    <apexchart
      type="radar"
      :height="height"
      :options="chartOptions"
      :series="series"
    />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

interface RadarPoint { label: string; value: number; tooltip?: string }
interface RadarSeries { name: string; color: string; data: RadarPoint[] }

interface Props {
  // Mode mono-série (rétro-compat)
  data?: RadarPoint[];
  color?: string;
  // Mode multi-séries (comparaison)
  series?: RadarSeries[];
  height?: number;
  maxLabelLength?: number;
}

const props = withDefaults(defineProps<Props>(), {
  data: undefined,
  color: '#3b82f6',
  series: undefined,
  height: 220,
  maxLabelLength: 15,
});

// Normalise vers une liste de séries, qu'on soit en mode `data` ou `series`.
const normalizedSeries = computed<RadarSeries[]>(() => {
  if (props.series && props.series.length) return props.series;
  return [{ name: '', color: props.color, data: props.data ?? [] }];
});

// Les catégories (axes) viennent de la 1re série ; en mode comparaison les séries
// sont déjà alignées côté backend (mêmes labels, même ordre).
const categories = computed(() => normalizedSeries.value[0]?.data.map((p) => p.label) ?? []);

function wrapLabel(label: string, maxLen: number): string | string[] {
  if (!maxLen || label.length <= maxLen) return label;
  const words = label.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (current && (current + ' ' + word).length > maxLen) { lines.push(current); current = word; }
    else { current = current ? current + ' ' + word : word; }
  }
  if (current) lines.push(current);
  return lines;
}

const series = computed(() =>
  normalizedSeries.value.map((s) => ({ name: s.name, data: s.data.map((p) => p.value) }))
);

const chartOptions = computed(() => ({
  chart: { toolbar: { show: false }, background: 'transparent', parentHeightOffset: 0 },
  grid: { padding: { top: -10, bottom: -15, left: 0, right: 0 } },
  colors: normalizedSeries.value.map((s) => s.color),
  // Au-delà de 2 séries superposées, les surfaces semi-transparentes se brouillent :
  // on passe en traits seuls (pas de remplissage) et on épaissit légèrement le trait.
  fill: { opacity: normalizedSeries.value.length > 2 ? 0 : 0.2 },
  stroke: { width: normalizedSeries.value.length > 2 ? 2.5 : 2 },
  markers: { size: 3, strokeWidth: 0 },
  xaxis: {
    categories: categories.value.map((label) => wrapLabel(label, props.maxLabelLength)),
    labels: { style: { colors: categories.value.map(() => '#9ca3af'), fontSize: '11px' } },
  },
  yaxis: { show: false, min: 0, max: 100, tickAmount: 4 },
  plotOptions: {
    radar: {
      size: 80,
      polygons: { strokeColors: '#374151', connectorColors: '#374151', fill: { colors: ['transparent'] } },
    },
  },
  tooltip: {
    enabled: true,
    theme: 'dark',
    y: {
      formatter: (val: number, opts?: { seriesIndex: number; dataPointIndex: number }) => {
        const point = opts ? normalizedSeries.value[opts.seriesIndex]?.data[opts.dataPointIndex] : undefined;
        return point?.tooltip ?? `${val}%`;
      },
    },
  },
  legend: { show: normalizedSeries.value.length > 1, position: 'bottom', offsetY: 12, labels: { colors: '#9ca3af' } },
}));
</script>
